import {
  KEY_LENGTH,
  MAX_PAYLOAD_SIZE,
  NACL_TAG_LENGTH,
} from './../../constants/encryption';
import {
  DISCRIMINATOR_LENGTH,
  FEE_LENGTH,
  FILTER_TAG_TOTAL_LENGTH,
  HINT_LENGTH,
} from './../../constants/envelope';
import { MESSAGE_TYPE_HANDSHAKE_CONFIRM, MESSAGE_TYPE_HANDSHAKE_REQUEST } from './../../constants/message';
import { sha256 } from 'js-sha256';
import { deriveSharedSecret } from '../crypto/deriveSharedSecret';
import { decryptLayer } from '../crypto/decryptLayer';
import { parseDataFrame } from './parseDataFrame';
import { UNLINK_PROGRAM_ID } from './../../constants/general';
import { uint8ArraysEqual } from './../helpers/compare';
import { extractProgramInstructionData } from '../helpers/extractProgramInstructionData';
import { deserializeMessageFragment } from '../serialization/deserializeMessageFragment';
import { MessageFragment } from '../../types/messageFragment';
import { VersionedTransactionResponse } from '@solana/web3.js';
import nacl from 'tweetnacl';
import { VerifySenderHmacProofInput } from "./../../types/crypto/verifySenderHmacProofInput";

type ReadMessageResult = {
  filterTag: Uint8Array;
  unpackedMessage: {
    conversationId: Uint8Array;
    feeLamports: bigint;
    senderProof: Uint8Array;
    senderNextViewPublicKey: Uint8Array;
    senderViewPublicKey: Uint8Array;
    message: MessageFragment;
  };
  ephemeralPublicKey: Uint8Array;
  sharedSecretHint: Uint8Array;
  receiverViewPublicKey: Uint8Array;
};

/**
 * Parses, decrypts, and verifies all Unlink messages from a given transaction.
 * This function performs local, stateless decoding of encrypted messages 
 * using provided view keypairs and optional Kyber shared secrets.
 * 
 * It supports a two-layer encryption model:
 *   - Layer 1 (required): Encrypted with an ECDH-derived key (from receiver view key and ephemeral key)
 *   - Layer 2 (optional): Encrypted with a hybrid key (Kyber shared secret + ECDH shared secret)
 * 
 * Decryption is attempted in reverse order of encryption: first ECDH, then optionally Kyber.
 * Each receiver view keypair is tested until one matches the shared secret hint and decrypts successfully.
 * 
 * After decryption, the sender's HMAC-based proof is verified to ensure authenticity.
 *
 * @param transaction - A previously fetched Solana transaction
 * @param receiverViewKeypairs - Array of candidate view keypairs (identity and ratcheted)
 * @param receiverKyberKeypairs - Array of ratcheted candidate kyber keypairs
 * @param verifySenderHmacProof - Verifies that the sender owns the claimed view key
 * @returns Array of successfully decrypted and authenticated message fragments
 */
export async function parseChunkedDataFromTransaction({
  transaction,
  receiverViewKeypairs,
  sessionKyberKeypairs,
  verifySenderHmacProof,
}: {
  transaction: VersionedTransactionResponse;
  receiverViewKeypairs: {
    viewPublicKey: Uint8Array;
    viewPrivateKey: Uint8Array;
  }[];
  sessionKyberKeypairs?: {
    kyberPublicKey: Uint8Array;
    kyberPrivateKey: Uint8Array;
    kyberSecret: Uint8Array;
  }[];
  verifySenderHmacProof: (input: VerifySenderHmacProofInput) => Promise<boolean>;
}): Promise<ReadMessageResult[]> {
  const result: ReadMessageResult[] = [];

  const instructions = extractProgramInstructionData(transaction, UNLINK_PROGRAM_ID.toBase58());

  // Minimum expected buffer size: one layer of ciphertext + metadata
  const minimumExpectedSize =
    DISCRIMINATOR_LENGTH + FEE_LENGTH + MAX_PAYLOAD_SIZE + NACL_TAG_LENGTH + KEY_LENGTH + HINT_LENGTH + FILTER_TAG_TOTAL_LENGTH;

  for (let a = 0; a < instructions.length; a++) {
    const buffer = instructions[a];

    if (buffer.length < minimumExpectedSize) {
      console.warn(`Instruction ${a} too short: expected >= ${minimumExpectedSize}, got ${buffer.length}`);
      continue;
    }

    const discriminator = buffer.readBigUInt64LE(0);

    // Parse anti-spam fee
    const feeLamports = buffer.readBigUInt64LE(DISCRIMINATOR_LENGTH);

    // Dynamically determine ciphertext bounds based on buffer length
    const ciphertextEnd = buffer.length - (KEY_LENGTH + HINT_LENGTH + FILTER_TAG_TOTAL_LENGTH);
    const ciphertext = new Uint8Array(buffer.slice(DISCRIMINATOR_LENGTH + FEE_LENGTH, ciphertextEnd));

    // Extract ephemeral public key
    const ephemeralPublicKey = new Uint8Array(
      buffer.slice(ciphertextEnd, ciphertextEnd + KEY_LENGTH)
    );

    // Extract shared secret hint
    const sharedSecretHint = new Uint8Array(
      buffer.slice(ciphertextEnd + KEY_LENGTH, ciphertextEnd + KEY_LENGTH + HINT_LENGTH)
    );

    // Extract filter tag
    const filterTag = new Uint8Array(
      buffer.slice(
        ciphertextEnd + KEY_LENGTH + HINT_LENGTH,
        ciphertextEnd + KEY_LENGTH + HINT_LENGTH + FILTER_TAG_TOTAL_LENGTH
      )
    );

    let finalDecrypted = null;
    let receiverViewPublicKey = null;
    let receiverViewPrivateKey = null;

    // Try each receiver view keypair in reverse order (most recent ratcheted keys likely last)
    decryptLoop: for (let i = receiverViewKeypairs.length - 1; i >= 0; i--) {
      receiverViewPublicKey = receiverViewKeypairs[i].viewPublicKey;
      receiverViewPrivateKey = receiverViewKeypairs[i].viewPrivateKey;

      const sharedSecret = deriveSharedSecret(receiverViewPrivateKey, ephemeralPublicKey);
      const fullHintHash = new Uint8Array(sha256.array(sharedSecret));
      const sharedSecretHintCalculated = fullHintHash.slice(0, HINT_LENGTH);

      if (!uint8ArraysEqual(sharedSecretHintCalculated, sharedSecretHint)) continue;

      // Try Layer 1 (ECDH)
      const innerDecrypted = decryptLayer(ciphertext, ephemeralPublicKey, sharedSecret, 1);
      if (!innerDecrypted) {
        console.warn(`Layer 1 (ECDH) decryption failed at instruction ${a}`);
        continue;
      }

      // Assume success with Layer 1
      finalDecrypted = innerDecrypted;
      const expectedLayer1Size = MAX_PAYLOAD_SIZE + NACL_TAG_LENGTH;
      const layer2Encrypted = finalDecrypted.length == expectedLayer1Size;
      
      // Optional Layer 2 (Kyber)
      if (layer2Encrypted && sessionKyberKeypairs?.length) {
        finalDecrypted = null;

        const validKyberSecrets = sessionKyberKeypairs
          .map(k => k.kyberSecret)
          .filter((s): s is Uint8Array => s instanceof Uint8Array && s.length > 0);

        if (validKyberSecrets.length > 0) {
          let kyberSucceeded = false;

          for (let i = validKyberSecrets.length - 1; i >= 0; i--) {
            const combinedSecret = new Uint8Array(64);
            combinedSecret.set(validKyberSecrets[i], 0);
            combinedSecret.set(sharedSecret, 32);

            const layer2Decrypted = decryptLayer(innerDecrypted, ephemeralPublicKey, combinedSecret, 2);
            if (layer2Decrypted) {
              finalDecrypted = layer2Decrypted;
              kyberSucceeded = true;              
              break;
            }
          }

          if (!kyberSucceeded) {
            // Decryption failed — discard the message
            console.warn(`Layer 2 (Kyber) decryption failed at instruction ${a}`);
            continue;
          }
        }
      }

      // Exit loop: successful match and decryption
      break decryptLoop;
    }
    
    if (!finalDecrypted || !receiverViewPublicKey || !receiverViewPrivateKey) {
      console.warn(`Failed to decrypt message at instruction ${a} with any available key`);
      continue;
    }

    // message was found
    const unpackedMessage = parseDataFrame(finalDecrypted);

    const verified = await verifySenderHmacProof({
      messageBytes: unpackedMessage.messageBytes,
      conversationId: unpackedMessage.conversationId,
      receiverViewPrivateKey,
      receiverViewPublicKey,
      senderPublicKey: unpackedMessage.senderViewPublicKey,
      senderNextViewPublicKey: unpackedMessage.senderNextViewPublicKey,
      proof: unpackedMessage.senderProof,
    });
    
    if (!verified) {
      console.warn(`Unverified message at instruction ${a}`);
      continue;
    }

    const fragment = deserializeMessageFragment(unpackedMessage.messageBytes);

    result.push({
      filterTag,
      unpackedMessage: {
        conversationId: unpackedMessage.conversationId,
        feeLamports,
        senderProof: unpackedMessage.senderProof,
        senderNextViewPublicKey: unpackedMessage.senderNextViewPublicKey,
        senderViewPublicKey: unpackedMessage.senderViewPublicKey,
        message: fragment,
      },
      ephemeralPublicKey,
      sharedSecretHint,
      receiverViewPublicKey,
    });
  }

  return result;
}